<template>
  <div class="confirmation">
    <div class="confirmation__container">
      <div class="confirmation__header">
        <div class="confirmation__check-icon">
          <FontAwesomeIcon :icon="['far', 'check-circle']" />
        </div>

        <h2 class="confirmation__title">
          {{
            isTicketPurchaseMode
              ? $t('landingpage.checkout-page.confirmation.order-title')
              : $t('landingpage.checkout-page.confirmation.title')
          }}
        </h2>
        <p class="confirmation__intro">
          {{ $t('landingpage.checkout-page.confirmation.intro') }}
        </p>
      </div>

      <template v-if="!isFree">
        <div class="confirmation__details">
          <div class="confirmation__total-info">
            <div class="confirmation__number">
              <p class="confirmation__number-label">
                {{ $t('landingpage.checkout-page.confirmation.order-number') }}
              </p>
              <p class="confirmation__number-value">
                {{ orderDetails?.order_number || '' }}
              </p>
            </div>
            <div class="confirmation__total">
              <p class="confirmation__number-label">
                {{ $t('landingpage.checkout-page.confirmation.total') }}
              </p>
              <p class="confirmation__number-value">
                {{
                  formatCurrencyLeft(
                    orderDetails?.total_gross_price || 0,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      .orderDetails?.currency,
                  )
                }}
              </p>
            </div>
          </div>

          <div
            v-if="bankTransferInstructions"
            class="confirmation__bank-transfer mt-4"
          >
            <p class="confirmation__ticket-items-label">
              {{
                $t('landingpage.checkout-page.confirmation.bank-transfer.title')
              }}
            </p>
            <p class="confirmation__bank-transfer-intro">
              {{
                $t('landingpage.checkout-page.confirmation.bank-transfer.intro')
              }}
            </p>
            <div class="confirmation__bank-transfer-details mt-2">
              <p>
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.account-holder',
                    )
                  }}:
                </strong>
                {{ bankTransferInstructions.account_holder_name }}
              </p>
              <p>
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.iban',
                    )
                  }}:
                </strong>
                {{ bankTransferInstructions.iban }}
              </p>
              <p v-if="bankTransferInstructions.bic">
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.bic',
                    )
                  }}:
                </strong>
                {{ bankTransferInstructions.bic }}
              </p>
              <p class="confirmation__bank-transfer-reference">
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.reference',
                    )
                  }}:
                </strong>
                {{ bankTransferInstructions.reference }}
              </p>
              <p class="confirmation__bank-transfer-note">
                {{
                  $t(
                    'landingpage.checkout-page.confirmation.bank-transfer.reference-note',
                  )
                }}
              </p>
              <p>
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.amount',
                    )
                  }}:
                </strong>
                {{ formattedBankTransferAmount }}
              </p>
              <p v-if="bankTransferInstructions.due_date">
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.due-by',
                    )
                  }}:
                </strong>
                {{ formatDate(bankTransferInstructions.due_date) }}
              </p>
              <p v-else>
                <strong>
                  {{
                    $t(
                      'landingpage.checkout-page.confirmation.bank-transfer.due-immediately',
                    )
                  }}
                </strong>
              </p>
            </div>
          </div>

          <template v-if="isTicketPurchaseMode">
            <div
              v-if="orderDetails?.line_items?.length"
              class="confirmation__ticket-items"
            >
              <p class="confirmation__ticket-items-label">
                {{ $t('landingpage.checkout-page.confirmation.tickets') }}
              </p>

              <template v-for="item in orderDetails?.line_items" :key="item.id">
                <p class="confirmation__ticket-item-value">
                  {{ item.quantity }}x {{ item.name[activeLocale] }}
                </p>
              </template>
            </div>

            <div
              v-if="hasBillingAddress"
              class="confirmation__billing-address mt-4"
            >
              <p class="confirmation__ticket-items-label">
                {{
                  $t('landingpage.checkout-page.confirmation.billing-address')
                }}
              </p>
              <div class="confirmation__ticket-item-value">
                <p v-if="orderDetails?.billing_company_name">
                  {{ orderDetails?.billing_company_name }}
                </p>
                <p>
                  {{ orderDetails?.billing_first_name }}
                  {{ orderDetails?.billing_last_name }}
                </p>
                <p>{{ orderDetails?.billing_address }}</p>
                <p>
                  {{ orderDetails?.billing_zip }}
                  {{ orderDetails?.billing_city }}
                </p>
                <p>{{ orderDetails?.billing_email }}</p>
              </div>
            </div>

            <TicketGuestManagement />
          </template>

          <template v-else>
            <p class="confirmation__section-title">
              {{
                $t(
                  'landingpage.checkout-page.confirmation.participant-overview',
                )
              }}
            </p>

            <!-- Main Guest Group -->
            <div class="confirmation__guest-group">
              <div class="flex items-center justify-between gap-x-5">
                <p class="confirmation__participant-label">
                  {{ $t('landingpage.checkout-page.confirmation.main-guest') }}
                </p>
                <p>
                  {{
                    `${mainGuest?.contact?.first_name || ''} ${mainGuest?.contact?.last_name || ''}`
                  }}
                </p>
              </div>

              <div
                v-if="getGuestLineItems(mainGuest)?.length"
                class="confirmation__guest-items"
              >
                <template
                  v-for="item in getGuestLineItems(mainGuest)"
                  :key="item.id"
                >
                  <div v-if="isNightsBookable(item)">
                    <p
                      class="confirmation__ticket-item-value flex items-center gap-2"
                    >
                      {{ item.quantity }}x {{ item.name[activeLocale] }}:
                      <span class="text-sm">
                        {{ formatDateWithWeekday(item?.option_from) }} -
                        {{ formatDateWithWeekday(item?.option_to) }} ({{
                          calculateNights(item?.option_from, item?.option_to)
                        }}
                        {{
                          calculateNights(
                            item?.option_from,
                            item?.option_to,
                          ) === 1
                            ? $t('common.night')
                            : $t('common.nights')
                        }})
                      </span>
                    </p>
                  </div>

                  <div v-else-if="isFlexibleBookable(item)">
                    <p class="confirmation__ticket-item-value">
                      {{ item.quantity }}x {{ item.name[activeLocale] }}:
                      <span class="text-sm">
                        {{
                          `${formatDate(item?.option_from, eventSettings?.timezone)}: ${formatTime(item?.option_from, eventSettings?.timezone, false)} - ${formatTime(item?.option_to, eventSettings?.timezone, false)} (${Math.round((new Date(item?.option_to) - new Date(item?.option_from)) / 60000)} min)`
                        }}
                      </span>
                    </p>
                  </div>

                  <p v-else class="confirmation__ticket-item-value">
                    {{ item.quantity }}x {{ item.name[activeLocale] }}
                  </p>
                </template>
              </div>
            </div>

            <!-- Companions -->
            <template v-if="isCompanionsEnabled">
              <div
                v-for="(companion, index) in companionGuests"
                :key="companion.id"
                class="confirmation__guest-group confirmation__guest-group--companion"
              >
                <div class="flex items-center justify-between gap-x-5">
                  <p class="confirmation__participant-label">
                    {{
                      $t('landingpage.checkout-page.confirmation.companion', {
                        num: index + 1,
                      })
                    }}
                  </p>
                  <p>
                    {{
                      `${companion?.contact?.first_name || ''} ${companion?.contact?.last_name || ''}`
                    }}
                  </p>
                </div>

                <div
                  v-if="getGuestLineItems(companion)?.length"
                  class="confirmation__guest-items"
                >
                  <template
                    v-for="item in getGuestLineItems(companion)"
                    :key="item.id"
                  >
                    <div v-if="isNightsBookable(item)">
                      <p
                        class="confirmation__ticket-item-value flex items-center gap-2"
                      >
                        {{ item.quantity }}x {{ item.name[activeLocale] }}:
                        <span class="text-sm">
                          {{ formatDateWithWeekday(item?.option_from) }} -
                          {{ formatDateWithWeekday(item?.option_to) }} ({{
                            calculateNights(item?.option_from, item?.option_to)
                          }}
                          {{
                            calculateNights(
                              item?.option_from,
                              item?.option_to,
                            ) === 1
                              ? $t('common.night')
                              : $t('common.nights')
                          }})
                        </span>
                      </p>
                    </div>

                    <div v-else-if="isFlexibleBookable(item)">
                      <p class="confirmation__ticket-item-value">
                        {{ item.quantity }}x {{ item.name[activeLocale] }}:
                        <span class="text-sm">
                          {{
                            `${formatDate(item?.option_from, eventSettings?.timezone)}: ${formatTime(item?.option_from, eventSettings?.timezone, false)} - ${formatTime(item?.option_to, eventSettings?.timezone, false)} (${Math.round((new Date(item?.option_to) - new Date(item?.option_from)) / 60000)} min)`
                          }}
                        </span>
                      </p>
                    </div>

                    <p v-else class="confirmation__ticket-item-value">
                      {{ item.quantity }}x {{ item.name[activeLocale] }}
                    </p>
                  </template>
                </div>
              </div>
            </template>
          </template>
        </div>
      </template>

      <template v-else>
        <div class="confirmation__details">
          <div class="confirmation__participant-item">
            <p class="confirmation__participant-label">
              {{ $t('landingpage.checkout-page.confirmation.main-guest') }}
            </p>
            <p class="confirmation__participant-value text-xl!">
              {{
                `${mainGuest?.contact?.first_name || ''} ${mainGuest?.contact?.last_name || ''}`
              }}
            </p>
          </div>

          <div
            v-if="getGuestFields(mainGuest, mainGuestFields)?.length"
            class="grid lg:grid-cols-2 gap-5"
          >
            <template
              v-for="field in getGuestFields(mainGuest, mainGuestFields)"
              :key="field.id"
            >
              <div class="confirmation__participant-item">
                <p class="confirmation__participant-label">
                  {{ field.field.label }}
                </p>
                <p class="confirmation__participant-value">
                  {{ mainGuest?.contact[field.field?.key] || '' }}
                </p>
              </div>
            </template>
          </div>

          <div
            v-if="guestHasTicket(mainGuest)"
            class="confirmation__participant-item"
          >
            <p class="confirmation__participant-label">
              {{ $t('landingpage.checkout-page.confirmation.ticket') }}
            </p>
            <p class="confirmation__participant-value mt-0.5">
              {{
                mainGuest?.items?.length
                  ? mainGuest.items.find(
                      (item) => item.product.type === 'ticket',
                    )?.product?.name[activeLocale]
                  : 'N/A'
              }}
            </p>
          </div>

          <div
            v-if="
              mainGuest?.items?.length &&
              mainGuest.items.filter((item) => item.product.type === 'addon')
                ?.length
            "
            class="confirmation__participant-item addon-item"
          >
            <p class="confirmation__participant-label">
              {{
                $t('landingpage.checkout-page.confirmation.additional-bookings')
              }}
            </p>

            <div class="flex flex-col mt-5">
              <template
                v-for="(group, index) in groupAddonItems(
                  mainGuest.items?.filter(
                    (item) => item.product.type === 'addon',
                  ) ?? [],
                )"
                :key="`${group.product.id}-${index}`"
              >
                <div class="confirmation__participant-value mt-0.5">
                  <template v-if="group.product.availability_type === 'nights'">
                    <span class="flex items-center gap-3">
                      <span
                        >• {{ `${group.items[0].quantity}x` }}
                        {{ group.product?.name[activeLocale] }}:</span
                      >

                      <span class="text-xs">
                        {{ formatDateWithWeekday(group.items[0]?.starts_at) }} -
                        {{ formatDateWithWeekday(group.items[0]?.ends_at) }} ({{
                          calculateNights(
                            group.items[0]?.starts_at,
                            group.items[0]?.ends_at,
                          )
                        }}
                        {{
                          calculateNights(
                            group.items[0]?.starts_at,
                            group.items[0]?.ends_at,
                          ) === 1
                            ? $t('common.night')
                            : $t('common.nights')
                        }})
                      </span>
                    </span>
                  </template>

                  <template
                    v-else-if="group.product.availability_type === 'flexible'"
                  >
                    <span>
                      •
                      {{
                        group.items.length > 1 ? `${group.items.length}x` : ''
                      }}
                      {{ group.product?.name[activeLocale] }}
                    </span>
                    <div
                      v-for="item in group.items"
                      :key="item.id"
                      class="ml-4 mt-1 text-xs"
                    >
                      {{
                        `${formatDate(item?.starts_at, eventSettings?.timezone)}: ${formatTime(item?.starts_at, eventSettings?.timezone, false)} - ${formatTime(item?.ends_at, eventSettings?.timezone, false)} (${Math.round((new Date(item?.ends_at) - new Date(item?.starts_at)) / 60000)} min)`
                      }}
                    </div>
                  </template>

                  <template v-else>
                    <span
                      >• {{ `${group.items[0].quantity}x` }}
                      {{ group.product?.name[activeLocale] }}</span
                    >
                  </template>
                </div>
              </template>
            </div>
          </div>
        </div>

        <template v-if="isCompanionsEnabled">
          <div
            v-for="(companion, index) in companionGuests"
            :key="companion.id"
            class="confirmation__details"
          >
            <div class="confirmation__participant-item">
              <p class="confirmation__participant-label">
                {{
                  $t('landingpage.checkout-page.confirmation.companion', {
                    num: index + 1,
                  })
                }}
              </p>
              <p class="confirmation__participant-value text-xl!">
                {{
                  `${companion?.contact.first_name || ''} ${companion?.contact.last_name || ''}`
                }}
              </p>
            </div>

            <div
              v-if="getGuestFields(companion, companionFields)?.length"
              class="grid lg:grid-cols-2 gap-5"
            >
              <template
                v-for="field in getGuestFields(companion, companionFields)"
                :key="field.id"
              >
                <div class="confirmation__participant-item">
                  <p class="confirmation__participant-label">
                    {{ field.field.label }}
                  </p>
                  <p class="confirmation__participant-value">
                    {{ companion?.contact[field.field?.key] || '' }}
                  </p>
                </div>
              </template>
            </div>

            <div
              v-if="guestHasTicket(companion)"
              class="confirmation__participant-item"
            >
              <p class="confirmation__participant-label">
                {{ $t('landingpage.checkout-page.confirmation.ticket') }}
              </p>
              <p class="confirmation__participant-value mt-0.5">
                {{
                  companion?.items?.length
                    ? companion.items.find(
                        (item) => item.product.type === 'ticket',
                      )?.product?.name[activeLocale]
                    : ''
                }}
              </p>
            </div>

            <div
              v-if="
                companion?.items?.length &&
                companion.items.filter((item) => item.product.type === 'addon')
                  ?.length
              "
              class="confirmation__participant-item addon-item"
            >
              <p class="confirmation__participant-label">
                {{
                  $t(
                    'landingpage.checkout-page.confirmation.additional-bookings',
                  )
                }}
              </p>
              <div class="flex flex-col mt-5">
                <template
                  v-for="(group, groupIndex) in groupAddonItems(
                    companion.items?.filter(
                      (item) => item.product.type === 'addon',
                    ) ?? [],
                  )"
                  :key="`${group.product.id}-${groupIndex}`"
                >
                  <div class="confirmation__participant-value mt-0.5">
                    <template
                      v-if="group.product.availability_type === 'nights'"
                    >
                      <span class="flex items-center gap-3">
                        <span
                          >• {{ `${group.items[0].quantity}x` }}
                          {{ group.product?.name[activeLocale] }}:</span
                        >

                        <span class="text-xs">
                          {{ formatDateWithWeekday(group.items[0]?.starts_at) }}
                          -
                          {{ formatDateWithWeekday(group.items[0]?.ends_at) }}
                          ({{
                            calculateNights(
                              group.items[0]?.starts_at,
                              group.items[0]?.ends_at,
                            )
                          }}
                          {{
                            calculateNights(
                              group.items[0]?.starts_at,
                              group.items[0]?.ends_at,
                            ) === 1
                              ? $t('common.night')
                              : $t('common.nights')
                          }})
                        </span>
                      </span>
                    </template>

                    <template
                      v-else-if="group.product.availability_type === 'flexible'"
                    >
                      <span>
                        •
                        {{
                          group.items.length > 1 ? `${group.items.length}x` : ''
                        }}
                        {{ group.product?.name[activeLocale] }}
                      </span>
                      <div
                        v-for="item in group.items"
                        :key="item.id"
                        class="ml-4 mt-1 text-xs"
                      >
                        {{
                          `${formatDate(item?.starts_at, eventSettings?.timezone)}: ${formatTime(item?.starts_at, eventSettings?.timezone, false)} - ${formatTime(item?.ends_at, eventSettings?.timezone, false)} (${Math.round((new Date(item?.ends_at) - new Date(item?.starts_at)) / 60000)} min)`
                        }}
                      </div>
                    </template>

                    <template v-else>
                      <span
                        >• {{ `${group.items[0].quantity}x` }}
                        {{ group.product?.name[activeLocale] }}</span
                      >
                    </template>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- <div class="confirmation__btn-container">
        <button
          type="button"
          class="confirmation__btn-cancel-registration btn btn-primary"
        >
          {{ $t('landingpage.checkout-page.confirmation.btn.cancel-registration') }}
        </button>
      </div> -->
    </div>
  </div>
</template>

<script setup>
import { inject, computed } from 'vue'
import { formatCurrencyLeft } from '@/composables/currency'
import { formatDate, formatTime } from '@/composables/date'
import { intlFormat } from 'date-fns'
import { useCurrentLocaleCode } from '@/composables/locale'
import TicketGuestManagement from '@/Components/LandingPage/Checkout/TicketGuestManagement.vue'

const orderDetails = inject('orderDetails')
const isCompanionsEnabled = inject('isCompanionsEnabled')
const mainGuest = inject('mainGuest')
const companionGuests = inject('companionGuests')
const isFree = inject('isFree')
const activeLocale = inject('activeLocale')
const mainGuestFields = inject('mainGuestFields')
const companionFields = inject('companionFields')
const eventSettings = inject('eventSettings')

const isTicketPurchaseMode = computed(() => {
  return orderDetails.value?.checkout_mode === 'ticket_purchase'
})

const hasBillingAddress = computed(() => {
  return (
    orderDetails.value?.billing_first_name ||
    orderDetails.value?.billing_last_name
  )
})

const bankTransferInstructions = computed(
  () => orderDetails.value?.bank_transfer_instructions ?? null,
)

const formattedBankTransferAmount = computed(() => {
  const instructions = bankTransferInstructions.value

  if (!instructions) {
    return ''
  }

  return formatCurrencyLeft(
    (instructions.amount_in_minor_unit ?? 0) / 100,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    (instructions.currency ?? 'EUR').toUpperCase(),
  )
})

const lineItemTypePriority = {
  TicketBookable: 0,
  nights: 1,
  flexible: 2,
  fixed: 3,
}

const getGuestLineItems = (guest) => {
  if (!guest?.items?.length || !orderDetails.value?.line_items?.length) {
    return []
  }

  const guestReservationIds = new Set(guest.items.map((item) => item.id))

  return orderDetails.value.line_items
    .filter((lineItem) =>
      lineItem.bookable_reservations?.some((res) =>
        guestReservationIds.has(res.id),
      ),
    )
    .sort((a, b) => {
      const getPriority = (item) => {
        if (item.product_type?.includes('TicketBookable')) {
          return lineItemTypePriority.TicketBookable
        }
        const availType =
          item.bookable_reservations?.[0]?.product?.availability_type
        return lineItemTypePriority[availType] ?? 3
      }
      return getPriority(a) - getPriority(b)
    })
}

const getGuestFields = (guest, contactFields) => {
  return contactFields?.items?.filter(
    (item) =>
      item.field &&
      guest?.contact[item.field.key] &&
      !['first_name', 'last_name'].includes(item.field.key),
  )
}

const calculateNights = (startDate, endDate) => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = end.getTime() - start.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const formatDateWithWeekday = (date) => {
  return intlFormat(
    new Date(date),
    {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    { locale: useCurrentLocaleCode() },
  )
}

const isNightsBookable = (item) => {
  return (
    item.bookable_reservations.length &&
    item.bookable_reservations[0].product?.availability_type === 'nights'
  )
}

const isFlexibleBookable = (item) => {
  return (
    item.bookable_reservations.length &&
    item.bookable_reservations[0].product?.availability_type === 'flexible'
  )
}

const addonTypePriority = { nights: 0, flexible: 1, fixed: 2 }

const groupAddonItems = (items) => {
  const groups = []
  const flexibleMap = {}

  for (const item of items) {
    if (item.product?.availability_type === 'flexible') {
      const productId = item.product.id
      if (!flexibleMap[productId]) {
        flexibleMap[productId] = { product: item.product, items: [] }
        groups.push(flexibleMap[productId])
      }
      flexibleMap[productId].items.push(item)
    } else {
      groups.push({ product: item.product, items: [item] })
    }
  }

  return groups.sort((a, b) => {
    const typeA = a.product?.availability_type ?? 'fixed'
    const typeB = b.product?.availability_type ?? 'fixed'
    return (addonTypePriority[typeA] ?? 2) - (addonTypePriority[typeB] ?? 2)
  })
}

const guestHasTicket = (guest) => {
  return (
    guest.items?.length &&
    guest.items?.find((item) => item.product?.type === 'ticket')
  )
}
</script>
